import { useTranslation } from 'react-i18next';
import { ActiveIndicator, ButtonGroup, ToggleButton } from './LiveMapActionButtons.styles';
import { PendingSpinnerIcon } from 'lib/components/PendingSpinnerIcon/PendingSpinnerIcon';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { Cv50Commands } from 'app/cross-cutting-concerns/communication/interfaces/pra-api-graphql';
import type { LocateCommandType } from 'app/modules/machine-inventory/machine-details-panel/state/machineDetailsPanelRobot/robotCleaningControl/types/RobotCleaningControl.types';

interface LiveMapActionButtonsProps {
  isBeepOn: boolean;
  isBlinkOn: boolean;
  /** Locked while a command is in flight or until the device has reported its Beep / Blink state. */
  isDisabled: boolean;
  pendingCommand: LocateCommandType | null;
  /** Presses switch the command off when it is on, and on otherwise. */
  onToggle: (commandType: LocateCommandType, isOn: boolean) => void;
}

export const LiveMapActionButtons = ({
  isBeepOn,
  isBlinkOn,
  isDisabled,
  pendingCommand,
  onToggle,
}: LiveMapActionButtonsProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <ButtonGroup>
      <ToggleButton
        type="button"
        $isActive={isBeepOn}
        aria-pressed={isBeepOn}
        aria-label={t('robotCleaningControl.beep.label')}
        title={t('robotCleaningControl.beep.label')}
        disabled={isDisabled}
        onClick={(): void => onToggle(Cv50Commands.Beep, isBeepOn)}
      >
        {pendingCommand === Cv50Commands.Beep ? <PendingSpinnerIcon /> : <SvgIcon name="beep" size={24} />}
        {isBeepOn && <ActiveIndicator />}
      </ToggleButton>
      <ToggleButton
        type="button"
        $isActive={isBlinkOn}
        aria-pressed={isBlinkOn}
        aria-label={t('robotCleaningControl.blink.label')}
        title={t('robotCleaningControl.blink.label')}
        disabled={isDisabled}
        onClick={(): void => onToggle(Cv50Commands.Blink, isBlinkOn)}
      >
        {pendingCommand === Cv50Commands.Blink ? <PendingSpinnerIcon /> : <SvgIcon name="lightOn" size={24} />}
        {isBlinkOn && <ActiveIndicator />}
      </ToggleButton>
    </ButtonGroup>
  );
};
